import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { journalEntryApi } from './api'
import type { JePayload } from './types'

export function useJournalEntries() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'journal-entries'),
    queryFn: journalEntryApi.list,
  })
}

export function useJournalEntry(id: number | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'journal-entries', id ?? 'none'),
    queryFn: () => journalEntryApi.get(id!),
    enabled: id !== null,
  })
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'journal-entries') })
}

export function useSaveJournalEntry() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: JePayload }) =>
      id == null ? journalEntryApi.create(body) : journalEntryApi.update(id, body),
    onSuccess: () => invalidateAll(qc, tenantId),
  })
}

export function usePostJournalEntry() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => journalEntryApi.post(id),
    onSuccess: () => invalidateAll(qc, tenantId),
  })
}

export function useDeleteJournalEntry() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => journalEntryApi.remove(id),
    onSuccess: () => invalidateAll(qc, tenantId),
  })
}
